import { useCallback } from "react"

import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import type { SimulationOutputIds } from "@/types_api/backend/transport/resimulate_simulation_outputs_request"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowDetailLike } from "@/types/RunRow"

import { normalizeRunRowDetail } from "../values"

const GET_SIMULATION_OUTPUT_OPERATION = "getSimulationOutput"

export function useSimulationOutputTransport<
  TMetricOutput extends RunMetricOutput = RunMetricOutput,
>(
  simulationOutputId: string | null | undefined,
  options?: {
    enabled?: boolean
    gcTime?: number
    refetchOnWindowFocus?: boolean
    refetchInterval?: number | false
    retry?: boolean | number
    staleTime?: number
  },
) {
  const enabled = Boolean(simulationOutputId) && (options?.enabled ?? true)

  return useTransportQuery({
    operation: GET_SIMULATION_OUTPUT_OPERATION,
    request: { simulation_output_id: simulationOutputId ?? "" },
    options: {
      enabled,
      gcTime: options?.gcTime,
      refetchOnWindowFocus: options?.refetchOnWindowFocus,
      refetchInterval: options?.refetchInterval,
      retry: options?.retry,
      staleTime: options?.staleTime,
      select: (response): RunRowDetailLike<TMetricOutput> | null =>
        response.simulation_output
          ? normalizeRunRowDetail<TMetricOutput>(response.simulation_output)
          : null,
    },
  })
}

export function useSimulationOutputTransportInvalidation() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(
    (simulationOutputId?: string | null) => {
      if (!simulationOutputId) {
        return transportQueryUtils.invalidateOperation(
          GET_SIMULATION_OUTPUT_OPERATION,
        )
      }

      return transportQueryUtils.invalidate(GET_SIMULATION_OUTPUT_OPERATION, {
        simulation_output_id: simulationOutputId,
      })
    },
    [transportQueryUtils],
  )
}

export function useSimulationOutputIdsTransport(
  runId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = Boolean(runId) && (options?.enabled ?? true)

  return useTransportQuery({
    operation: "listSimulationOutputIds",
    request: { run_id: runId ?? "" },
    options: {
      enabled,
      select: (response) => response.simulation_output_ids,
    },
  })
}

export function useSimulationOutputIdsFetcherTransport() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(
    async (runId: string) => {
      const result = await transportQueryUtils.fetch(
        "listSimulationOutputIds",
        {
          run_id: runId,
        },
      )
      return result.simulation_output_ids
    },
    [transportQueryUtils],
  )
}

export function useDeleteSimulationOutputTransport(options?: {
  onError?: (error: { message?: string }) => void
  onSuccess?: () => void
}) {
  const transportQueryUtils = useTransportQueryUtils()

  return useTransportMutation("deleteSimulationOutput", {
    ...options,
    onSuccess: async (_data, variables) => {
      await Promise.all([
        transportQueryUtils.invalidate("getSimulationOutput", {
          simulation_output_id: variables.simulation_output_id,
        }),
        transportQueryUtils.invalidateOperation("listSimulationOutputIds"),
      ])
      options?.onSuccess?.()
    },
  })
}

export function useSetSimulationOutputPublicTransport(
  options?: Parameters<
    typeof useTransportMutation<"setSimulationOutputPublic">
  >[1],
) {
  return useTransportMutation("setSimulationOutputPublic", options)
}

export function useResimulateSimulationOutputsTransport() {
  return useTransportMutation("resimulateSimulationOutputs")
}

export function toSimulationOutputIds(
  simulationOutputIds: string[],
): SimulationOutputIds {
  return simulationOutputIds as SimulationOutputIds
}
